import { usePaginatedList } from 'components/Commons/PaginatedList'
import SearchBar from 'components/Commons/Search'
import useUploadDrop from 'hooks/upload/useUploadDrop'
import PictureSearch, {
  MAX_IMAGE_SIZE,
  usePictureSearchInterface,
} from 'components/Commons/PaginatedList/PictureSearch'
import useFileUpload from 'components/Commons/PaginatedList/PictureSearch/hooks/useFileUpload'
import SearchSvg from 'assets/svg/search.svg?react'
import Cross from 'assets/svg/close.svg?react'

import pictureSearchTranslations from 'components/Commons/PaginatedList/PictureSearch/translations'

import {
  Clear,
  Container,
  DropActiveContainer,
  Icon,
  Image,
  ImageLabel,
  LeftPanelContainer,
  PreviewWrapper,
  RightPanelContainer,
  Separator,
} from './styled'
import { useState } from 'react'
import { FormattedMessage } from 'components/Commons/Translation'

export const FILTERS = {
  SEARCH: 'searchText',
  IMAGE: 'pictureVectorSearchImageUrl',
}

const SORT_BY_UPDATED = { updatedAt: 'DESC' }

const IMAGE_SEARCH_LEFT_WIDTH = 180
const IMAGE_SEARCH_RIGHT_WIDTH = 70

const UPLOAD_DROP_OPTIONS = {
  maxSize: MAX_IMAGE_SIZE,
  signed: true,
  notifyOnReject: false,
  noClick: true,
}

function RightPanel({ value, onClear, imageSearchValue, controls }) {
  const showClearOrSeparator = imageSearchValue || value.length >= 1
  return (
    <RightPanelContainer>
      {showClearOrSeparator && (
        <>
          <Clear dataTestId="cross_clear" onClick={onClear} component={Cross} />
          <Separator />
        </>
      )}
      <PictureSearch controls={controls} />
    </RightPanelContainer>
  )
}

function CurrentFilterImage({ imageActive, pictureSize }) {
  const { filters, setFiltersAndSort } = usePaginatedList()
  const { pictureVectorSearchImageUrl } = filters || {}

  function handleClick() {
    const newFilter = { [FILTERS.IMAGE]: undefined }
    setFiltersAndSort({ filters: newFilter, sort: SORT_BY_UPDATED })
  }

  if (!pictureVectorSearchImageUrl) return null

  return (
    <PreviewImage
      imageActive={imageActive}
      imageUrl={pictureVectorSearchImageUrl}
      onClick={handleClick}
      pictureSize={pictureSize}
    />
  )
}

function PreviewImage({ imageUrl, onClick, imageActive, pictureSize }) {
  return (
    <PreviewWrapper imageActive={imageActive} onClick={onClick}>
      <Image src={imageUrl} alt={imageUrl} pictureSize={pictureSize} sizes="30px" />
      <ImageLabel>{imageUrl}</ImageLabel>
    </PreviewWrapper>
  )
}

function LeftPanel({ imageActive, pictureSize }) {
  return (
    <LeftPanelContainer>
      <Icon component={SearchSvg} />
      <CurrentFilterImage imageActive={imageActive} pictureSize={pictureSize} />
    </LeftPanelContainer>
  )
}

function getLayoutSize(size) {
  if (size === 'sm') {
    return { dragBoxHeight: 40, pictureSize: 20 }
  }
  return { dragBoxHeight: 50, pictureSize: 30 }
}

function SearchWithImageDrop({ className, placeholder, maxLength = null, size }) {
  const [isImageSelected, setisImageSelected] = useState(false)
  const { filters, setFiltersAndSort, setFilters } = usePaginatedList()
  const controls = usePictureSearchInterface()

  const { acceptedFileTypes, onUploadComplete, onUploadFailed, customFileValidator, onUploadRejected } = useFileUpload()

  const { getRootProps, getInputProps, isDragActive } = useUploadDrop({
    accept: acceptedFileTypes,
    onUploadComplete,
    onUploadFailed,
    customFileValidator,
    onUploadRejected,
    ...UPLOAD_DROP_OPTIONS,
    disabled: controls.tooltip.isOpen,
  })

  /**
   * Nested DropZones:
   * 1. Searchbar
   * 2. Picture icon tooltip
   *
   * Problem: Concurrent activation of `isDragActive` in both searchbar and tooltip.
   *
   * Solution: After attempts to prevent event propagation and testing settings like `noDragEventsBubbling: true`,
   *           the effective approach was to check the tooltip's `isOpen` state to show the drop active message.
   */
  const showDropActive = !controls.tooltip.isOpen && isDragActive

  const value = filters[FILTERS.SEARCH] ?? ''
  const imageSearchValue = filters[FILTERS.IMAGE]

  function onChange(value) {
    if (isImageSelected) {
      setisImageSelected(false)
    }
    setFilters({ [FILTERS.SEARCH]: value })
  }

  function onClear() {
    const sort = imageSearchValue ? SORT_BY_UPDATED : undefined
    setFiltersAndSort({ filters: { [FILTERS.SEARCH]: '', [FILTERS.IMAGE]: undefined }, sort })
  }

  function handleKeyDown(e) {
    // If the user presses backspace and the search bar is empty, we make the image "pill" selected
    // If the user presses backspace again with the image "pill" selected, we remove the image filter
    if (e.key === 'Backspace' && value === '' && imageSearchValue) {
      if (isImageSelected) {
        setisImageSelected(false)
        setFiltersAndSort({
          filters: { [FILTERS.IMAGE]: undefined },
          sort: SORT_BY_UPDATED,
        })
      } else {
        setisImageSelected(true)
      }
    }
  }

  const innerLeftWidth = imageSearchValue ? IMAGE_SEARCH_LEFT_WIDTH : 0
  const outerRightWidth = IMAGE_SEARCH_RIGHT_WIDTH

  const { dragBoxHeight, pictureSize } = getLayoutSize(size)

  return (
    <Container {...getRootProps()}>
      {showDropActive ? (
        <DropActiveContainer dragBoxHeight={dragBoxHeight}>
          <FormattedMessage {...pictureSearchTranslations.dropImageHere} />
        </DropActiveContainer>
      ) : (
        <SearchBar
          className={className}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          maxLength={maxLength}
          size={size}
          innerLeftWidth={innerLeftWidth}
          outerRightWidth={outerRightWidth}
          right={<RightPanel value={value} onClear={onClear} imageSearchValue={imageSearchValue} controls={controls} />}
          left={<LeftPanel imageActive={isImageSelected} pictureSize={pictureSize} />}
          onKeyDown={handleKeyDown}
          dataTestId="input_search_with_image_drop"
        />
      )}
      <input id={'image_search_bar_input'} {...getInputProps()} />
    </Container>
  )
}

export default SearchWithImageDrop
